import axios from 'axios';

// Use environment variable with fallback to port 8000
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

console.log('🔧 API Configuration:', {
  baseURL: API_BASE_URL,
  environment: process.env.NODE_ENV,
  debug: process.env.REACT_APP_DEBUG
});

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 30000
});

// Add request logging
apiClient.interceptors.request.use(
  (config) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('🔵 API Request:', config.method.toUpperCase(), config.url);
    }
    return config;
  },
  (error) => {
    console.error('❌ Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response logging
apiClient.interceptors.response.use(
  (response) => {
    if (process.env.REACT_APP_DEBUG === 'true') {
      console.log('✅ API Response:', response.status, response.data);
    }
    return response;
  },
  (error) => {
    console.error('❌ API Error:', error.message);
    
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      
      // Handle 401 Unauthorized (Session expired)
      if (error.response.status === 401) {
        console.log('Session expired, clearing session and redirecting to login...');
        // Clear all session-related data
        localStorage.removeItem('ltms_session');
        localStorage.removeItem('sessionData'); // Clear legacy key too
        // Force reload to login page to clear React state
        window.location.href = '/login';
        return Promise.reject(error);
      }
      
      // Handle 403 Forbidden (No permission)
      if (error.response.status === 403) {
        console.error('Access denied:', error.response.data.message);
      }
    } else if (error.request) {
      console.error('❌ No response received from server');
      console.error(`💡 Backend not reachable at ${API_BASE_URL}`);
      console.error('💡 Check if PHP server is running on the correct port');
      console.error('💡 Run: php -S localhost:8000 (in backend directory)');
    }
     
    return Promise.reject(error);
  }
);

export const loginUser = async (username, password) => {
  try {
    const response = await apiClient.post('/auth/login.php', {
      username,
      password
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { 
      success: false, 
      message: 'Network error - Cannot reach backend server. Please ensure the PHP server is running on port 8000.' 
    };
  }
};

export const getFinancialYears = async (companyId) => {
  try {
    const response = await apiClient.get('/company/get-financial-years.php', {  // ✅ GET
      params: {
        companyid: companyId
      }
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const createSession = async (userId, companyId, yearId) => {
  try {
    const response = await apiClient.post('/auth/create-session.php', {
      userid: userId,
      companyid: companyId,
      yearid: yearId
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const logoutUser = async () => {
  try {
    const response = await apiClient.post('/logout.php');
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const verifySession = async () => {
  try {
    const response = await apiClient.get('/verify-session.php');
    return response.data;
  } catch (error) {
    // Don't throw on 401 - let the interceptor handle it
    if (error.response?.status === 401) {
      return { success: false, message: 'Session expired' };
    }
    throw error.response?.data || { success: false, message: 'Network error' };
  }
};

export const getUserMenus = async () => {
  try {
    const response = await apiClient.get('/menus.php');
    return response.data;
  } catch (error) {
    throw error.response?.data || { success: false, message: 'Failed to load menus' };
  }
};

export default apiClient;